import { useState } from 'react';
import { useSEO } from '@/hooks/useSEO';

export default function AboutPage() {
  useSEO({
    title: '关于我们 - 神马视频 | 水果视频品牌聚合平台',
    description: '神马视频是领先的水果视频品牌聚合平台，汇集香蕉视频、红桃视频、蜜桃视频、樱桃视频等品牌。了解神马视频的品牌故事、口碑与联系方式。',
    keywords: '神马视频,关于神马视频,视频平台,品牌介绍,联系方式,水果视频品牌',
  });

  const breadcrumbs = [
    { name: '首页', href: '/' },
    { name: '关于我们', href: '/about' },
  ];

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
          <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500">
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.href} className="flex items-center gap-2">
                {i > 0 && <i className="ri-arrow-right-s-line"></i>}
                {i < breadcrumbs.length - 1 ? (
                  <a href={crumb.href} className="hover:text-primary-600 transition-colors">{crumb.name}</a>
                ) : (
                  <span className="text-foreground-700 font-medium">{crumb.name}</span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* 品牌介绍 */}
        <section className="mb-14 md:mb-20">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-4">
              关于神马视频
            </h1>
            <p className="text-sm text-foreground-500 text-center mb-10">
              水果视频品牌聚合平台 · 高清免费在线观看
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20video%20streaming%20platform%20office%20with%20large%20screens%20displaying%20colorful%20movie%20posters%2C%20trendy%20creative%20workspace%20with%20warm%20lighting%2C%20young%20team%20collaborating%2C%20minimalist%20design%2C%20professional%20atmosphere%20with%20plant%20decorations%2C%20bright%20and%20airy&width=800&height=600&seq=about-brand-shenma&orientation=landscape"
                  alt="神马视频品牌形象"
                  className="w-full rounded-lg"
                  title="神马视频 - 水果视频品牌聚合平台"
                />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground-900 font-heading mb-4">
                  品牌故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  神马视频成立于2024年，是领先的水果视频品牌聚合平台。我们致力于为用户提供香蕉视频、红桃视频、蜜桃视频、樱桃视频等各大水果视频品牌的高清影视内容免费在线观看服务。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  平台汇聚海量电影、电视剧、综艺、动漫、纪录片等内容，采用智能CDN加速技术，确保用户享受流畅不缓冲的观影体验。无论是热门大片还是经典老片，在神马视频都能找到。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的团队由一群热爱影视的年轻人组成，始终秉持"让好内容被更多人看到"的使命，不断优化产品体验，为用户带来极致的在线观影享受。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 核心优势 */}
        <section className="mb-14 md:mb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-10">
              为什么选择神马视频
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-movie-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground-900">四大品牌聚合</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  集合香蕉视频、红桃视频、蜜桃视频、樱桃视频，一个平台看遍水果视频品牌内容
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-4k-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground-900">高清不缓冲</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  支持4K超清画质，全球CDN加速覆盖，播放流畅零等待
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-refresh-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground-900">每日更新</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  海量影视资源每日更新，永远有新鲜内容等着你发现
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-shield-check-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground-900">完全免费</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  无需注册无需付费，所有内容免费在线观看
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 口碑评价 */}
        <section className="mb-14 md:mb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "神马视频真心好用！四个品牌的视频都能看，画质清晰还不卡顿，已经推荐给身边所有朋友了。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-xs font-semibold text-secondary-700">
                    张
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">张先生</p>
                    <p className="text-xs text-foreground-500">资深影迷</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "每天下班就打开神马视频追剧，资源丰富更新快，界面简洁用着很舒服，强烈推荐！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-accent-200 flex items-center justify-center text-xs font-semibold text-accent-700">
                    李
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">李女士</p>
                    <p className="text-xs text-foreground-500">追剧达人</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "找了很久的纪录片在神马视频上看到了，太惊喜了！分类清晰搜索方便，观影体验满分。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-200 flex items-center justify-center text-xs font-semibold text-primary-700">
                    王
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">王先生</p>
                    <p className="text-xs text-foreground-500">纪录片爱好者</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14 md:mb-20">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              <details className="bg-background-100 rounded-lg group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 select-none">
                  神马视频需要付费吗？
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  不需要。神马视频是一个完全免费的在线视频聚合平台，所有内容均可免费在线观看，无需注册会员，无需支付任何费用。
                </div>
              </details>
              <details className="bg-background-100 rounded-lg group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 select-none">
                  支持哪些视频品牌？
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  神马视频目前支持香蕉视频、红桃视频、蜜桃视频、樱桃视频四大水果视频品牌的内容聚合，覆盖电影、电视剧、综艺、动漫、纪录片等多种类型。
                </div>
              </details>
              <details className="bg-background-100 rounded-lg group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 select-none">
                  视频播放卡顿怎么办？
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  神马视频采用智能CDN加速技术，会根据您的网络环境自动选择最佳节点。如遇卡顿，建议检查本地网络连接，或尝试切换清晰度。
                </div>
              </details>
              <details className="bg-background-100 rounded-lg group">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 select-none">
                  内容多久更新一次？
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  神马视频的内容每日更新，热门电影、电视剧、综艺节目会在第一时间上线，确保用户不错过任何精彩内容。
                </div>
              </details>
            </div>
          </div>
        </section>

        {/* 联系我们 */}
        <section id="contact" className="max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-10">
            联系我们
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">电子邮箱</h3>
                  <p className="text-sm text-foreground-500 mt-1">service@shenmashipin.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                  <i className="ri-customer-service-line text-lg text-accent-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">在线客服</h3>
                  <p className="text-sm text-foreground-500 mt-1">工作日 9:00 - 18:00</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">公司地址</h3>
                  <p className="text-sm text-foreground-500 mt-1">中国 · 北京</p>
                </div>
              </div>
            </div>

            <ContactForm />
          </div>
        </section>
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((crumb, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": crumb.name,
            "item": `https://www.shenmashipin.com${crumb.href}`
          }))
        })
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "神马视频需要付费吗？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "不需要。神马视频是一个完全免费的在线视频聚合平台，所有内容均可免费在线观看，无需注册会员，无需支付任何费用。"
              }
            },
            {
              "@type": "Question",
              "name": "支持哪些视频品牌？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "神马视频目前支持香蕉视频、红桃视频、蜜桃视频、樱桃视频四大水果视频品牌的内容聚合。"
              }
            },
            {
              "@type": "Question",
              "name": "视频播放卡顿怎么办？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "神马视频采用智能CDN加速技术，会根据您的网络环境自动选择最佳节点。如遇卡顿，建议检查本地网络连接，或尝试切换清晰度。"
              }
            },
            {
              "@type": "Question",
              "name": "内容多久更新一次？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "神马视频的内容每日更新，热门电影、电视剧、综艺节目会在第一时间上线。"
              }
            }
          ]
        })
      }} />
    </>
  );
}

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9n3mcgbdkpk99j7gg0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== 'phone_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam') || msg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="contact" className="bg-background-100 rounded-lg p-5 md:p-6 space-y-4">
      <h3 className="text-base font-semibold text-foreground-900 font-heading">发送消息</h3>
      <div>
        <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1.5">姓名</label>
        <input
          id="contact-name"
          name="name"
          type="text"
          required
          className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1.5">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
          placeholder="请输入您的留言（500字以内）"
        ></textarea>
      </div>
      <div className="hp-field">
        <label htmlFor="phone_alt">备用电话</label>
        <input id="phone_alt" name="phone_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>
      <button
        type="submit"
        disabled={formStatus === 'loading'}
        className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
      >
        {formStatus === 'loading' ? '发送中...' : '发送消息'}
      </button>
      {formStatus === 'success' && (
        <p className="text-xs text-green-600 text-center">消息已发送，我们会尽快回复您！</p>
      )}
      {formStatus === 'error' && formError && (
        <p className="text-xs text-red-500 text-center">{formError}</p>
      )}
    </form>
  );
}